import type { TOC } from '@ember/component/template-only';
import { get } from '@ember/helper';
import UiCard from 'garaje/components/ui/card';
import formatPhone from 'garaje/helpers/format-phone';
import sortStrings from 'garaje/helpers/sort-strings';

interface IdentifiersSignature {
  Args: {
    identifiers: {
      emails: string[];
      aliases: string[];
      'phone-numbers': string[];
    } | null;
  };
  Element: HTMLDivElement;
}

interface SectionSignature {
  Args: {
    title: string;
    items: string[];
    emptyMessage: string;
    testId: string;
    formatItem?: (item: string) => string;
  };
}

const Section: TOC<SectionSignature> = <template>
  <div class="flex flex-col gap-1" data-test-identifier-section={{@testId}}>
    <div class="flex justify-between items-center px-1">
      <h2 class="text-sm font-medium text-carbon-60">{{@title}}</h2>
      <span class="text-sm font-medium text-carbon-30">{{@items.length}}</span>
    </div>
    <UiCard
      @border={{true}}
      class="flex flex-col border-carbon-10 rounded-lg bg-carbon-2 px-4 max-h-64 overflow-y-auto"
    >
      {{#each (sortStrings @items) as |item index|}}
        <div
          class="py-2 first:pt-0 last:pb-0 flex items-center {{if index 'border-t border-carbon-10'}}"
          data-test-identifier-row
        >
          <span class="text-sm text-carbon-100 truncate block">
            {{if @formatItem (@formatItem item) item}}
          </span>
        </div>
      {{else}}
        <div class="py-1" data-test-identifier-empty>
          <span class="text-sm text-carbon-50 italic">{{@emptyMessage}}</span>
        </div>
      {{/each}}
    </UiCard>
  </div>
</template>;

export default <template>
  {{#if @identifiers}}
    <div class="flex flex-col gap-4 max-w-xl" ...attributes>
      {{#if @identifiers.aliases.length}}
        <Section @title="Names" @items={{@identifiers.aliases}} @emptyMessage="No aliases found" @testId="aliases" />
      {{/if}}

      {{#if @identifiers.emails.length}}
        <Section @title="Emails" @items={{@identifiers.emails}} @emptyMessage="No emails found" @testId="emails" />
      {{/if}}

      {{#let (get @identifiers "phone-numbers") as |phoneNumbers|}}
        {{#if phoneNumbers.length}}
          <Section
            @title="Phone numbers"
            @items={{phoneNumbers}}
            @emptyMessage="No phone numbers found"
            @testId="phone-numbers"
            @formatItem={{formatPhone}}
          />
        {{/if}}
      {{/let}}
    </div>
  {{/if}}
</template> satisfies TOC<IdentifiersSignature>;
